import {
    FlowchartDataMetric,
    FlowchartDataSubTotal,
    FlowchartDataTotal,
    ReferencedCalendarDefinition,
    ReferencedMediaHierarchyDefinition,
    ReferencedTotalsDefinition,
} from '@omniflow/omni-webapi';
import { BuildEngine } from '../models/build-engine';
import { Block } from '../models/block';
import MainHeadBlock from '../blocks/right-hand-totals/main-head-block';
import HeadBlock from '../blocks/right-hand-totals/head-block';
import ValueBlock from '../blocks/right-hand-totals/value-block';
import TotalBlock from '../blocks/right-hand-totals/total-block';
import { CalendarHelper } from '../helpers/calendar-helper';
import { StyleMapper } from '../models/styles';
import { TFlowchartDataLevelFlat, checkCompositeKeyInAncestors, accumulateNestedTotals, hasDescendantsInAncestorSet } from '../helpers/level-helper';
import { TColumn } from '../../../lib/utils/getColumnsFromDataDictonary';
import { FormatKeyInfo } from '../models/format-key-info';
import { formatNumberFromDataType } from '../helpers/number-format-helper';
import { sum, filter, isEmpty } from 'lodash';
import { mergeAndSortMetrics, TMergedMetrics } from '../helpers/merge-sort-helper';

export interface IRightHandTotalsBuilderProps {
    clientId: string;
    ReferencedTotalsDefinition?: ReferencedTotalsDefinition;
    ReferencedMediaHierarchyDefinition?: ReferencedMediaHierarchyDefinition;
    FlatLevelData?: TFlowchartDataLevelFlat[];
    ReferencedCalendarDefinition?: ReferencedCalendarDefinition;
    CalendarHelper?: CalendarHelper;
    calendarOverlayHeight?: number;
    dataColumns?: TColumn[];
    currencyCode?: string;
    showSubtotalsAtBottom?: boolean;
    inflightOverlayMetrics?: string[];
    isDecimal?: boolean;
    ApplyToCurrencyMetric?: boolean;
    ApplyToNonCurrencyMetric?: boolean;

    /**
     * Contains all levels related to the split selection including:
     * - The selected levels themselves
     * - All ancestors (parents) of selected levels
     * - All descendants (children) of selected levels
     * Used for filtering the hierarchy to show only relevant rows
     */
    ancestorSet?: Set<string>;

    /**
     * Contains ONLY the levels explicitly selected for split-by operation
     * (does NOT include ancestors or descendants)
     * Used to determine which rows should display values vs accumulated values
     * Example: If user selects "Vehicle: Facebook", this contains only that levelId
     */
    splitLevelIds?: Set<string>;
}


export class RightHandTotalsBuilder extends BuildEngine {
    clientId: string;
    ReferencedTotalsDefinition: ReferencedTotalsDefinition;
    ReferencedMediaHierarchyDefinition: ReferencedMediaHierarchyDefinition;
    FlatLevelData: TFlowchartDataLevelFlat[];
    ReferencedCalendarDefinition: ReferencedCalendarDefinition;
    CalendarHelper: CalendarHelper;
    calendarOverlayHeight: number;

    rightHandTotalsColumnLength: number = 0;
    rightHandTotalsRowLength: number = 0;
    inflightOverlayMetrics?: string[];
    isDecimal?: boolean;
    ApplyToCurrencyMetric?: boolean;
    ApplyToNonCurrencyMetric?: boolean;

    /**
     * Contains all levels related to split selection (selected + ancestors + descendants)
     * See interface documentation for details
     */
    ancestorSet?: Set<string> = new Set<string>();

    /**
     * Contains ONLY the levels explicitly selected for split-by
     * See interface documentation for details
     */
    splitLevelIds?: Set<string> = new Set<string>();

    private dataColumns: TColumn[] = [];

    /**
     * Performance optimization: Map for O(1) parent lookups instead of O(n) .find() calls
     * Built once and reused throughout the builder
     */
    private parentMap?: Map<string, TFlowchartDataLevelFlat>;

    /**
     * Performance optimization: Cache parent chains to avoid redundant traversals
     * Key: levelId, Value: array of parent IDs from immediate parent to root
     */
    private parentChainCache = new Map<string, string[]>();

    /**
     * Builds and returns the parent lookup map for O(1) access
     * This map is built once and cached for reuse
     * @returns Map of levelId to TFlowchartDataLevelFlat
     */
    private getOrBuildParentMap(): Map<string, TFlowchartDataLevelFlat> {
        if (!this.parentMap) {
            this.parentMap = new Map<string, TFlowchartDataLevelFlat>();
            this.FlatLevelData.forEach(item => {
                if (item.levelId) {
                    this.parentMap!.set(item.levelId, item);
                }
            });
        }
        return this.parentMap;
    }

    /**
     * Gets the parent chain for a given levelId (from immediate parent to root)
     * Results are cached to avoid redundant traversals
     * @param levelId The level ID to get parent chain for
     * @returns Array of parent IDs from immediate parent to root
     */
    private getParentChain(levelId: string): string[] {
        if (this.parentChainCache.has(levelId)) {
            return this.parentChainCache.get(levelId)!;
        }

        const chain: string[] = [];
        const parentMap = this.getOrBuildParentMap();
        let current = parentMap.get(levelId);

        while (current?.parentId) {
            chain.push(current.parentId);
            current = parentMap.get(current.parentId);
        }

        this.parentChainCache.set(levelId, chain);
        return chain;
    }

    /**
     * Checks the relationship between a row and the split selection
     * Optimized version using parent chain cache instead of repeated .find() calls
     * @param data The level data to check
     * @param splitSet The set of selected split level IDs
     * @returns Object indicating if row is directly selected or has ancestor in selection
     */
    private checkSplitRelationship(
        data: TFlowchartDataLevelFlat,
        splitSet: Set<string>
    ): { inSplit: boolean; ancestorInSplit: boolean } {
        // Case 1: This row itself is directly in the split selection
        if (splitSet.has(data.levelId)) {
            return { inSplit: true, ancestorInSplit: false };
        }

        // Case 2: Check if any ancestor is in split selection using cached parent chain
        const parentChain = this.getParentChain(data.levelId);
        const ancestorInSplit = parentChain.some(parentId => splitSet.has(parentId));

        return { inSplit: false, ancestorInSplit };
    }

    /**
     * Cleanup method to clear caches and prevent memory leaks
     * Should be called after build() completes
     */
    private cleanup(): void {
        this.parentChainCache.clear();
        this.parentMap?.clear();
        this.parentMap = undefined;
    }

    private calculateSubTotalSum(subTotal: FlowchartDataSubTotal): number {
        let sum = 0;
        if (subTotal?.Metrics) {
            subTotal.Metrics.forEach((metric) => {
                if (metric.ValueJson !== undefined) {
                    const value = JSON.parse(metric.ValueJson);
                    sum += value || 0;
                }
            });
        }
        return sum;
    }
    private getInflightOverlaySum = (inflightOverlays: string[][], inflightOverlayIndex: number): number => {
        return sum(inflightOverlays.map(c => c && c[inflightOverlayIndex] && JSON.parse(c[inflightOverlayIndex]) != null ? parseFloat(JSON.parse(c[inflightOverlayIndex])) : 0).flat());
    }

    private calculateFlightBarTotalSum(metrics: FlowchartDataMetric[]): number {
        let sum = 0;
        if (metrics?.length > 0) {
            metrics.forEach((metric) => {
                if (metric.ValueJson !== undefined) {
                    const value = JSON.parse(metric.ValueJson);
                    sum += value || 0;
                }
            });
        }
        return sum;
    }

    private formatKeyInfo: FormatKeyInfo[] = [];
    currencyCode?: string;
    showSubtotalsAtBottom?: boolean;
    constructor({
        clientId,
        ReferencedTotalsDefinition,
        ReferencedMediaHierarchyDefinition,
        FlatLevelData,
        ReferencedCalendarDefinition,
        CalendarHelper,
        calendarOverlayHeight,
        dataColumns,
        currencyCode,
        showSubtotalsAtBottom,
        inflightOverlayMetrics,
        isDecimal,
        ApplyToCurrencyMetric,
        ApplyToNonCurrencyMetric,
        ancestorSet,
        splitLevelIds
    }: IRightHandTotalsBuilderProps) {
        super();

        this.clientId = clientId;
        this.ReferencedTotalsDefinition = ReferencedTotalsDefinition;
        this.ReferencedMediaHierarchyDefinition =
            ReferencedMediaHierarchyDefinition;
        this.FlatLevelData = FlatLevelData;
        this.ReferencedCalendarDefinition = ReferencedCalendarDefinition;
        this.CalendarHelper = CalendarHelper;
        this.calendarOverlayHeight = calendarOverlayHeight ?? 0;
        this.dataColumns = dataColumns ?? [];
        this.currencyCode = currencyCode;
        this.showSubtotalsAtBottom = showSubtotalsAtBottom;
        this.inflightOverlayMetrics = inflightOverlayMetrics;
        this.isDecimal = isDecimal;
        this.ApplyToCurrencyMetric = ApplyToCurrencyMetric;
        this.ApplyToNonCurrencyMetric = ApplyToNonCurrencyMetric;
        this.ancestorSet = ancestorSet;
        this.splitLevelIds = splitLevelIds;

    }

    build = async (): Promise<Block[][]> => {
        let rows: Block[][] = [];

        if (
            this.ReferencedTotalsDefinition?.Definition &&
            this.ReferencedMediaHierarchyDefinition?.Definition &&
            this.FlatLevelData &&
            this.ReferencedCalendarDefinition?.Definition &&
            this.CalendarHelper
        ) {
            try {
                rows = await this.buildRightHandTotals();
            } finally {
                // Clean up caches to prevent memory leaks
                this.cleanup();
            }
        }

        return rows;
    };
    private createValueBlock(value: string, totalIndex: number): ValueBlock {
        const block = new ValueBlock({ value });
        const styling = this.ReferencedTotalsDefinition.Definition.Columns[totalIndex].Styling;
        block.getDirectStyles = () => StyleMapper.mapStyleToExcelStyle(styling, false);
        return block;
    }

    private buildSubTotalRow(
        subTotal: FlowchartDataSubTotal,
        data: TFlowchartDataLevelFlat,
        totalColumns: any[],
        isValidCurrency: boolean
    ): Block[] {
        const row = new Array<Block>(totalColumns.length);
        const sum = this.calculateSubTotalSum(subTotal);
        const currency = isValidCurrency ? this.currencyCode : data.LocalCurrency;
        const totals = data.Totals || [];

        for (let i = 0; i < totalColumns.length; i++) {
            const currentTotal = totals[i];
            if (currentTotal?.ColumnName === subTotal.ColumnName &&
                currentTotal?.TableId === subTotal.TableId) {
                const sumFormatted = formatNumberFromDataType(
                    sum,
                    subTotal.TableId,
                    subTotal.ColumnName,
                    this.dataColumns,
                    currency,
                    this.isDecimal,
                    this.ApplyToCurrencyMetric,
                    this.ApplyToNonCurrencyMetric,
                );
                row[i] = this.createValueBlock(sumFormatted, i);
            } else {
                row[i] = this.createValueBlock('', i);
            }
        }

        return row;
    }
    private addRowToCollection(
        row: Block[],
        depth: number,
        rows: Block[][],
        stack: [[Block[]]]
    ): void {
        if (this.showSubtotalsAtBottom) {
            if (!stack[depth]) {
                stack[depth] = [[]];
            }
            stack[depth].push(row);
        } else {
            rows.push(row);
        }
    }

    private processSubTotalSummaries(
        data: TFlowchartDataLevelFlat,
        totalColumns: any[],
        rows: Block[][],
        subTotalSummaryStack: [[Block[]]],
        isValidCurrency: boolean
    ): void {
        if (!data.SubTotalSummary?.length) return;

        const settingsObj = data.LevelDefinition?.Settings?.find(s => s.Order === data.Order);
        const processedOrders = new Set<number>();

        for (const subTotal of data.SubTotalSummary) {
            // Skip already processed orders
            if (processedOrders.has(subTotal.Order)) continue;

            const displayNames = settingsObj?.SubTotalSummary?.[subTotal.Order]?.DisplayNames ?? {};
            const hasDisplayNames = Object.keys(displayNames).length > 0;

            if (hasDisplayNames) {
                // Get all subtotals with this order once
                const subTotals = filter(data.SubTotalSummary, { Order: subTotal.Order }) || [];
                processedOrders.add(subTotal.Order);

                for (const [displayKey, _] of Object.entries(displayNames)) {
                    // Find matching subtotal
                    let matchingSubTotal = null;
                    for (const st of subTotals) {
                        if (displayKey.includes(st.ColumnName) && displayKey.includes(st.DisplayName)) {
                            matchingSubTotal = st;
                            break;
                        }
                    }

                    if (matchingSubTotal) {
                        const subTotalRow = this.buildSubTotalRow(
                            matchingSubTotal, data, totalColumns, isValidCurrency
                        );
                        this.addRowToCollection(subTotalRow, data.Depth, rows, subTotalSummaryStack);
                    }
                }
            } else {
                // Process normal subtotal
                const subTotalRow = this.buildSubTotalRow(subTotal, data, totalColumns, isValidCurrency);
                this.addRowToCollection(subTotalRow, data.Depth, rows, subTotalSummaryStack);
            }
        }
    }
    buildRightHandTotals = async (): Promise<Block[][]> => {
        const rows: Block[][] = [];
        let isValidCurrency = this.currencyCode && this.currencyCode != 'LLL';

        // Get total column names
        let totalColumns = [];
        let totalsData = [];
        this.FlatLevelData.forEach((data) => {
            if (!totalColumns.length && data.Totals?.length) {
                totalColumns = data.Totals.map(({ Name }) => Name);
                totalsData = data.Totals;
            }
        });

        // Add main totals headline
        rows.push([
            new MainHeadBlock({
                value: 'Totals',
                rowStart: -2 - this.calendarOverlayHeight,
                columnLength: totalColumns.length,
            }),
        ]);

        // Add total column name headlines
        const totalsRow = [];
        totalColumns.forEach((totalColumn, index) => {
            const headerBlock = new HeadBlock({
                value: totalColumn,
            });

            headerBlock.getDirectStyles = () =>
                StyleMapper.mapStyleToExcelStyle(
                    this.ReferencedTotalsDefinition.Definition.Columns[index]
                        .HeaderStyling,
                    false
                );

            totalsRow.push(headerBlock);
        });
        rows.push(totalsRow);

        // Add blank rows if we have a calendar overlay
        if (this.calendarOverlayHeight > 0) {
            Array.from({ length: this.calendarOverlayHeight }).forEach(() => {
                const calendarOverlayRow = [];

                Array.from({ length: totalColumns.length }).forEach(
                    (x, totalIndex) => {
                        const valueBlock = new ValueBlock({
                            value: '',
                        });
                        valueBlock.getDirectStyles = () =>
                            StyleMapper.mapStyleToExcelStyle(
                                this.ReferencedTotalsDefinition.Definition
                                    .Columns[totalIndex].Styling,
                                false
                            );

                        calendarOverlayRow.push(valueBlock);
                    }
                );

                rows.push(calendarOverlayRow);
            });
        }

        // ============================================================================
        // MAIN ROW PROCESSING LOOP
        // ============================================================================
        // For each level in the flattened hierarchy, we build a row of totals values.
        // The logic handles:
        // 1. Regular mode: All rows show their values, totals accumulate from depth-1 rows
        // 2. Split-by mode: Only selected rows and their hierarchy show values
        // 3. Subtotal rendering: Inline or bottom-positioned based on settings
        // 4. SubtotalSummary rendering: Additional rows for grouped subtotal displays
        // ============================================================================

        // Add total column values and collect total sums
        let totalSums = [];
        let totalSumsRows = [];
        let localCurrency = this.FlatLevelData.find(c => c.LocalCurrency)?.LocalCurrency;
        const subTotalStack: [[Block[]]] = [[[]]];
        const subTotalSummaryStack: [[Block[]]] = [[[]]];
        let depthCursor = 0;

        // accumulateNestedTotals is now imported from level-helper.ts
        this.FlatLevelData.forEach((data) => {

            if (this.showSubtotalsAtBottom) {
                if (subTotalStack.length && data.Depth <= depthCursor) {
                    // subTotalStack.forEach((subTotalBlock: Block) => {
                    //     rows.push([subTotalBlock]);
                    //  })
                    //subTotalStack.length = 0;
                    for (let i = depthCursor; i >= data.Depth; i--) {
                        if (subTotalStack.length && subTotalStack[i]?.length) {
                            while (subTotalStack[i].length) {
                                const tempValue = subTotalStack[i].pop();
                                if (tempValue.length) {
                                    rows.push(tempValue);

                                }
                            }
                        }
                    }


                }
                if (subTotalSummaryStack.length && data.Depth <= depthCursor) {
                    for (let i = depthCursor; i >= data.Depth; i--) {
                        if (subTotalSummaryStack.length && subTotalSummaryStack[i]?.length) {
                            while (subTotalSummaryStack[i].length) {
                                const tempValue = subTotalSummaryStack[i].pop();
                                if (tempValue.length) {
                                    rows.push(tempValue);

                                }
                            }
                        }
                    }
                }
            }
            // Add an empty value block, if inflight overlays are activated
            if (data?.Metrics?.[0]?.InflightOverlays?.length && data?.Metrics?.[0]?.InflightOverlays.filter((val, index) => data?.Metrics.some(c => c.InflightOverlays && c.InflightOverlays[index] && JSON.parse(c.InflightOverlays[index]) != null))?.length) {
                /*if (data?.Metrics?.[0]?.InflightOverlayValueJson) {*/
                data.Metrics[0]?.InflightOverlays.forEach((inflightOverlay, inflightOverlayIndex) => {
                    const inflightOverlayRow = [];
                    let isDataExists = data.Metrics.some(c => c.InflightOverlays && c.InflightOverlays.length && c.InflightOverlays[inflightOverlayIndex] && c.InflightOverlays[inflightOverlayIndex] != null && JSON.parse(c.InflightOverlays[inflightOverlayIndex]) != null);
                    if (isDataExists) {
                        Array.from({ length: totalColumns.length }).forEach(
                            (x, totalIndex) => {
                                const currentTotal = data.Totals?.[totalIndex] ?? totalsData[totalIndex];
                                const currentInflightOverlay = data.LevelDefinition?.Settings.find(c =>
                                    c.Order == data.Order)?.InflightOverlays?.[inflightOverlayIndex];
                                if (currentTotal?.ColumnName == currentInflightOverlay?.ColumnName && currentTotal?.TableId == currentInflightOverlay?.TableId) {
                                    const isInflightOverlayMetric = data.LevelDefinition?.Settings.find(c =>
                                        c.Order == data.Order)?.InflightOverlays?.[inflightOverlayIndex].ColumnName &&
                                        this.inflightOverlayMetrics.includes(
                                            data.LevelDefinition?.Settings.find(c =>
                                                c.Order == data.Order)?.InflightOverlays?.[inflightOverlayIndex].ColumnName.toLowerCase()
                                        );
                                    const levelInflightOverlays = data.LevelDefinition?.Settings.find(c =>
                                        c.Order == data.Order)?.InflightOverlays;
                                    const inflightOverlayIndexes = levelInflightOverlays && levelInflightOverlays.length ? levelInflightOverlays.map((c, index) => {
                                        return this.inflightOverlayMetrics.includes(c.ColumnName) ? index : -1;
                                    }) : [];
                                    const tempData = { ...data };
                                    const mergedMetrics = isInflightOverlayMetric ? mergeAndSortMetrics(tempData.Metrics, tempData.FlightRange, inflightOverlayIndexes) : {} as TMergedMetrics;
                                    const sum = isInflightOverlayMetric ? this.getInflightOverlaySum(mergedMetrics.map(i => i.InflightOverlays), inflightOverlayIndex) : 0;

                                    const inflightOverlaySelected = data.LevelDefinition?.Settings.find(c =>
                                        c.Order == data.Order)?.InflightOverlays?.[inflightOverlayIndex];
                                    const sumFormatted = isInflightOverlayMetric ? formatNumberFromDataType(
                                        sum,
                                        inflightOverlaySelected.TableId,
                                        inflightOverlaySelected.ColumnName,
                                        this.dataColumns,
                                        isValidCurrency ? this.currencyCode : data.LocalCurrency,
                                        this.isDecimal,
                                        this.ApplyToCurrencyMetric,
                                        this.ApplyToNonCurrencyMetric,
                                    ) : null;
                                    const valueBlock = new ValueBlock({
                                        value: sumFormatted,
                                    });
                                    valueBlock.getDirectStyles = () =>
                                        StyleMapper.mapStyleToExcelStyle(
                                            this.ReferencedTotalsDefinition.Definition
                                                .Columns[totalIndex].Styling,
                                            false
                                        );

                                    inflightOverlayRow.push(valueBlock);
                                } else {
                                    const valueBlock = new ValueBlock({
                                        value: '',
                                    });
                                    valueBlock.getDirectStyles = () =>
                                        StyleMapper.mapStyleToExcelStyle(
                                            this.ReferencedTotalsDefinition.Definition.Columns[totalIndex].Styling,
                                            false
                                        );
                                    inflightOverlayRow.push(valueBlock);
                                }
                            }
                        );
                    }
                    if (inflightOverlayRow.length) {
                        rows.push(inflightOverlayRow);
                    }
                });
            }
            // Add total column values
            const totalRow = [];
            Array.from({ length: totalColumns.length }).forEach(
                (x, totalIndex) => {
                    const total =
                        data?.Totals?.[totalIndex] ??
                        ({ ValuesJson: [] } as FlowchartDataTotal);
                    const { ValuesJson } = total;

                    let sum = 0;
                    if (data?.IsSplitRow) {
                        if (total?.ColumnName == data?.MetricColumnName && total?.TableId == data?.TableId) {
                            sum = this.calculateFlightBarTotalSum(data.Metrics);
                        } else if (data?.IsLastDepth) {
                            ValuesJson.forEach((ValueJson) => {
                                sum += JSON.parse(ValueJson);
                            });
                        }
                    } else {
                        ValuesJson.forEach((ValueJson) => {
                            sum += JSON.parse(ValueJson);
                        });
                    }

                    // ============================================================================
                    // SPLIT-BY SCENARIO LOGIC (PERFORMANCE OPTIMIZED)
                    // ============================================================================
                    // When a user selects specific levels to "split by" (e.g., specific vehicles),
                    // we need to show totals only for those selected levels and their hierarchy.
                    // This logic ensures:
                    // 1. Selected rows show their individual values
                    // 2. Parent rows show accumulated values from selected descendants
                    // 3. Child rows (sub-levels, splits) of selected rows show their values
                    // 4. Unrelated rows show $0
                    // 5. Grand total equals sum of only the selected rows (no double-counting)
                    //
                    // PERFORMANCE: Uses Map-based lookups (O(1)) instead of .find() (O(n))
                    // and caches parent chains to avoid redundant traversals
                    // ============================================================================

                    let totalsForSplitByColumn = 0;  // Fixed typo: was "Spilt"
                    let useSplitLogic = false;

                    // Only use split logic if splitLevelIds is explicitly provided
                    // splitLevelIds contains ONLY the levels explicitly selected for split-by
                    // (does NOT include ancestors or descendants)
                    // ancestorSet is used separately for hierarchy filtering only
                    if (this.splitLevelIds && this.splitLevelIds.size > 0) {
                        // Use optimized method instead of inline function
                        const { inSplit, ancestorInSplit } = this.checkSplitRelationship(data, this.splitLevelIds);

                        if (inSplit) {
                            // CASE A: This row is directly in the split selection
                            // Example: User selected "Vehicle: Facebook" and this is that row
                            // Action: Show this row's value and add it to the grand total
                            totalsForSplitByColumn = sum;
                            totalSums[totalIndex] = (totalSums[totalIndex] ?? 0) + sum;
                            useSplitLogic = true;
                        } else if (ancestorInSplit) {
                            // CASE B: This row's ancestor is in the split selection
                            // Example: User selected "Vehicle: Facebook" and this is a sub-level or split row under it
                            // Action: Show this row's value but DON'T add to grand total (parent already counted it)
                            totalsForSplitByColumn = sum;
                            useSplitLogic = true;
                        } else if (this.ancestorSet && this.ancestorSet.has(data.levelId)) {
                            // CASE C: This row is an ancestor/parent of the split selection
                            // Example: User selected "Vehicle: Facebook" and this is the "Channel: Social" parent row
                            // Action: Accumulate and show the sum of all selected descendants
                            // Note: Don't add to grand total (descendants will add themselves)

                            // PERFORMANCE: Use parent chain cache instead of repeated traversals
                            this.getOrBuildParentMap();

                            // Iterate through all flat level data to find descendants
                            this.FlatLevelData.forEach(flatItem => {
                                // Only process items that are in the split selection
                                if (this.splitLevelIds.has(flatItem.levelId)) {
                                    // Use cached parent chain to check if flatItem is a descendant of current row
                                    // This replaces the O(n) .find() loop with O(1) Map lookups
                                    const parentChain = this.getParentChain(flatItem.levelId);
                                    const isDescendant = parentChain.includes(data.levelId);

                                    // If this is a descendant, add its value to the accumulator
                                    if (isDescendant) {
                                        const totals = flatItem.Totals || [];
                                        const totalValue = totals[totalIndex];  // Direct index access instead of .find()
                                        if (totalValue && totalValue.ValuesJson) {
                                            const childSum = totalValue.ValuesJson.reduce((acc, sumValue) => acc + parseFloat(sumValue), 0);
                                            totalsForSplitByColumn += childSum;
                                        }
                                    }
                                }
                            });
                            useSplitLogic = true;
                        } else {
                            // CASE D: This row has no relationship to the split selection
                            // Example: User selected "Vehicle: Facebook" and this is "Channel: TV" (unrelated)
                            // Action: Show $0
                            totalsForSplitByColumn = 0;
                            useSplitLogic = true;
                        }
                    }

                    if (!useSplitLogic) {
                        if (data.Depth == 1 || totalSumsRows[totalIndex] == data.Depth) {
                            if (totalSums[totalIndex] === undefined) {
                                totalSums[totalIndex] = 0;
                            }
                            totalSums[totalIndex] += sum;
                        }
                    }

                    if (totalSumsRows[totalIndex] === undefined) {
                        totalSumsRows[totalIndex] = 0;
                    }
                    totalSumsRows[totalIndex] = totalSums[totalIndex] == 0 ? data.Depth + 1 : totalSumsRows[totalIndex] != 0 ?
                        data.Depth : totalSumsRows[totalIndex];

                    this.formatKeyInfo[totalIndex] = {
                        Index: totalIndex,
                        TableId: data.TableId,
                        ColumnName: total.ColumnName,
                    };

                    const sumFormatted = formatNumberFromDataType(
                        useSplitLogic ? totalsForSplitByColumn : sum,
                        data.TableId,
                        total.ColumnName,
                        this.dataColumns,
                        isValidCurrency ? this.currencyCode : data.LocalCurrency,
                        this.isDecimal,
                        this.ApplyToCurrencyMetric,
                        this.ApplyToNonCurrencyMetric,
                    );

                    const valueBlock = new ValueBlock({
                        value: sumFormatted,
                    });
                    valueBlock.getDirectStyles = () =>
                        StyleMapper.mapStyleToExcelStyle(
                            this.ReferencedTotalsDefinition.Definition.Columns[
                                totalIndex
                            ].Styling,
                            false
                        );

                    totalRow.push(valueBlock);
                }
            );
            rows.push(totalRow);

            if (data.SubTotals?.length) {
                data.SubTotals.forEach((subTotal) => {
                    const subTotalsRow = [];
                    Array.from({ length: totalColumns.length }).forEach((x, totalIndex) => {
                        const currentTotal = data.Totals?.[totalIndex];
                        const currentSubTotal = subTotal;
                        if (currentTotal?.ColumnName == currentSubTotal?.ColumnName && currentTotal?.TableId == currentSubTotal?.TableId) {
                            const sum = this.calculateSubTotalSum(subTotal);
                            const sumFormatted = formatNumberFromDataType(
                                sum,
                                subTotal.TableId,
                                subTotal.ColumnName,
                                this.dataColumns,
                                isValidCurrency ? this.currencyCode : data.LocalCurrency,
                                this.isDecimal,
                                this.ApplyToCurrencyMetric,
                                this.ApplyToNonCurrencyMetric,
                            );

                            const valueBlock = new ValueBlock({
                                value: sumFormatted,
                            });
                            valueBlock.getDirectStyles = () =>
                                StyleMapper.mapStyleToExcelStyle(
                                    this.ReferencedTotalsDefinition.Definition
                                        .Columns[totalIndex].Styling,
                                    false
                                );

                            subTotalsRow.push(valueBlock);
                        } else {
                            const valueBlock = new ValueBlock({
                                value: '',
                            });
                            valueBlock.getDirectStyles = () =>
                                StyleMapper.mapStyleToExcelStyle(
                                    this.ReferencedTotalsDefinition.Definition.Columns[totalIndex].Styling,
                                    false
                                );
                            subTotalsRow.push(valueBlock);
                        }
                    }
                    );
                    if (this.showSubtotalsAtBottom) {
                        if (!subTotalStack[data.Depth]) {
                            subTotalStack[data.Depth] = [[]];
                        }
                        subTotalStack[data.Depth].push(subTotalsRow);
                    }
                    else {
                        rows.push(subTotalsRow);
                    }

                });

            }

            if (data.SubTotalSummary?.length) {
                this.processSubTotalSummaries(
                    data, totalColumns, rows, subTotalSummaryStack, isValidCurrency
                );
            }

            if (this.showSubtotalsAtBottom) {
                depthCursor = data.Depth;
            }
        });
        if (subTotalStack.length) {
            for (let i = subTotalStack.length - 1; i >= 1; i--) {
                if (subTotalStack.length && subTotalStack[i]?.length) {
                    while (subTotalStack[i].length) {
                        const tempValue = subTotalStack[i].pop();
                        if (tempValue.length) {
                            rows.push(tempValue);

                        }
                    }
                }
            }
        }

        if (subTotalSummaryStack.length) {
            for (let i = subTotalSummaryStack.length - 1; i >= 1; i--) {
                if (subTotalSummaryStack.length && subTotalSummaryStack[i]?.length) {
                    while (subTotalSummaryStack[i].length) {
                        const tempValue = subTotalSummaryStack[i].pop();
                        if (tempValue.length) {
                            rows.push(tempValue);

                        }
                    }
                }
            }
        }
        // Add total sums
        const totalSumsRow = [];
        totalSums.forEach((totalSum, index) => {
            const sumFormatted = formatNumberFromDataType(
                totalSum,
                this.formatKeyInfo[index].TableId,
                this.formatKeyInfo[index].ColumnName,
                this.dataColumns,
                isValidCurrency ? this.currencyCode : localCurrency,
                this.isDecimal,
                this.ApplyToCurrencyMetric,
                this.ApplyToNonCurrencyMetric,
            );

            const totalBlock = new TotalBlock({
                value: sumFormatted,
            });

            totalBlock.getDirectStyles = () =>
                StyleMapper.mapStyleToExcelStyle(
                    this.ReferencedTotalsDefinition.Definition.Columns[index]
                        .SumStyling,
                    false
                );

            totalSumsRow.push(totalBlock);
        });
        rows.push(totalSumsRow);

        this.rightHandTotalsColumnLength = this.getColumnLength(rows);
        this.rightHandTotalsRowLength = this.getRowLength(rows);

        return rows;
    };
}